import React, { useEffect, useState } from "react";
import qscoinprice from "../../data/qscoinprice";
import DiscountBanner from "../DiscountBanner";
import Select from 'react-select';

const CPPCalculator = () => {
  // State initialization with localStorage loading
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [pixelAmount, setPixelAmount] = useState(() => {
    const saved = localStorage.getItem('cppCalculator_pixelAmount');
    return saved ? parseFloat(saved) : 1;
  });
  const [coinsSpent, setCoinsSpent] = useState(() => {
    const saved = localStorage.getItem('cppCalculator_coinsSpent');
    return saved ? parseInt(saved) : 0;
  });
  const [coinprices, setcoinPrices] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    localStorage.setItem('cppCalculator_pixelAmount', pixelAmount.toString());
    localStorage.setItem('cppCalculator_coinsSpent', coinsSpent.toString());
    
    if (selectedBundle) {
      localStorage.setItem('cppCalculator_selectedBundle', JSON.stringify({
        value: selectedBundle.value,
        label: selectedBundle.label
      }));
    } else {
      localStorage.removeItem('cppCalculator_selectedBundle');
    }
  }, [selectedBundle, pixelAmount, coinsSpent, isInitialized]);

  // Load coin prices and initialize state
  useEffect(() => {
    const fetchData = async () => {
      try {
        const coins = [];

        for (const key in qscoinprice) {
          const item = qscoinprice[key];
          if (item.itemid.startsWith("itm_coin")) {
            coins.push(item);
          }
        }

        setcoinPrices(coins);
        
        // Load saved bundle selection after prices are loaded
        const savedBundle = localStorage.getItem('cppCalculator_selectedBundle');
        if (savedBundle) {
          const parsedBundle = JSON.parse(savedBundle);
          const matchingBundle = coins.find(coin => coin.id === parsedBundle.value);
          if (matchingBundle) {
            setSelectedBundle({
              value: matchingBundle.id,
              label: `${matchingBundle.name} (${matchingBundle.price} $PIXEL)`,
              data: matchingBundle
            });
          }
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.log("Issue setting coin prices for calculator: ", error);
        setIsInitialized(true);
      }
    };

    fetchData();
  }, []);

  // Calculate CPP values
  const calculateValues = () => {
    if (!selectedBundle) return null;

    const baseCPP = selectedBundle.data.amount / selectedBundle.data.price;
    const discountedCPP = baseCPP * 0.95; // 5% discount

    const coinValue = pixelAmount * baseCPP;
    const coinValueDiscounted = pixelAmount * discountedCPP;

    const pixelValue = coinsSpent / baseCPP;
    const pixelValueDiscounted = coinsSpent / discountedCPP;

    return {
      baseCPP,
      discountedCPP,
      coinValue,
      coinValueDiscounted,
      pixelValue,
      pixelValueDiscounted,
      profitLoss: coinValue - coinsSpent,
      profitLossDiscounted: coinValueDiscounted - coinsSpent,
    };
  };

  const results = calculateValues();

  return (
    <div className="calculator-style">
      <h2 className="calculator-title">Coin Per Pixel Calculator</h2>

      <div className="calculator-project">
        {/* Bundle Selection */}
        <div className="calc-card">
          <div className="space-y-2">
            <label className="block mb-2 font-medium">Pixel Bundle</label>
            <Select
              options={coinprices?.map((bundle) => ({
                value: bundle.id,
                label: `${bundle.name} (${bundle.price} $PIXEL)`,
                data: bundle,
              }))}
              value={selectedBundle}
              onChange={setSelectedBundle}
              placeholder="Select $PIXEL bundle..."
              className="calculator-form"
            />
          </div>
        </div>

        {selectedBundle && (
          <>
            {/* CPP Display */}
            <div className="grid grid-cols-2 gap-4 mt-2 mb-4">
              <div className="calc-card">
                <div className="">Base CPP</div>
                <div className="text-xl font-bold">{results.baseCPP?.toLocaleString()} coins</div>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-100">
                <div className="text-sm text-green-600">CPP with 5% Discount</div>
                <div className="text-xl font-bold text-green-700">{results.discountedCPP?.toLocaleString()} coins</div>
              </div>
            </div>

            {/* Pixel to Coin Conversion */}
            <div className="bg-[var(--panel-section-bg)] m-2 p-2 rounded-xl">
              <label className="block mb-2 font-medium">$PIXEL Earned</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  value={pixelAmount}
                  onChange={(e) => setPixelAmount(parseFloat(e.target.value) || 0)}
                  className="flex-1 p-2 border rounded calculator-form"
                />
                <span className="flex items-center px-3 tags">$PIXEL</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 mb-2">
                <div className="calc-card">
                  <div className="text-xs">Base Value</div>
                  <div>{(results.coinValue || 0).toLocaleString()} coins</div>
                </div>
                <div className="bg-green-50 p-2 rounded border border-green-100">
                  <div className="text-xs text-green-700">With 5% Discount</div>
                  <div className="font-semibold text-green-700">{(results.coinValueDiscounted || 0).toLocaleString()} coins</div>
                </div>
              </div>
            </div>

            {/* Coin to Pixel Conversion */}
            <div className="bg-[var(--panel-section-bg)] m-2 p-2 rounded-xl">
              <label className="block mb-2 font-medium">Coins Spent</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={coinsSpent}
                  onChange={(e) => setCoinsSpent(parseInt(e.target.value) || 0)}
                  className="flex-1 p-2 border rounded calculator-form"
                />
                <span className="flex items-center px-3 tags">Coins</span>
              </div>
            </div>

            {/* Profit/Loss Display */}
            {coinsSpent > 0 && (
              <div
                className="bg-[var(--panel-section-bg)] m-2 p-2 rounded-xl"
                style={{
                  backgroundColor: results.profitLoss >= 0 ? "#f0fdf4" : "#fef2f2",
                  borderColor: results.profitLoss >= 0 ? "#bbf7d0" : "#fecaca",
                }}
              >
                <div className="text-center font-medium mb-1 text-[var(--text4-color)]">Potential Profit/Loss</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <div className="text-xs text-gray-500">Base Rate</div>
                    <div className={results.profitLoss >= 0 ? "text-green-600" : "text-red-600"}>
                      {results.profitLoss >= 0 ? "+" : ""}
                      {results.profitLoss.toLocaleString()} coins
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500">With 5% Discount</div>
                    <div className={results.profitLossDiscounted >= 0 ? "text-green-600" : "text-red-600"}>
                      {results.profitLossDiscounted >= 0 ? "+" : ""}
                      {results.profitLossDiscounted.toLocaleString()} coins
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="text-center m-2">
        <DiscountBanner />
      </div>
    </div>
  );
};

export default CPPCalculator;